import { useCamaraFotos } from '@/components/fotos/CamaraFotos';
import { GaleriaFotos } from '@/components/fotos/GaleriaFotos';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAutoresFotos, useColaFotos, useObrasFotos } from '@/hooks/useFotos';
import type { FiltroGaleria } from '@/lib/cliente';
import { reintentarErrores } from '@/lib/colaFotos';
import { cn } from '@/lib/utils';
import { TIPOS_CC } from '@/pages/admin/CentrosCostoFotosPantalla';
import type { TipoCentroCosto } from '@adelante/shared';
import {
  Buildings,
  Camera,
  CaretRight,
  CircleNotch,
  CloudArrowUp,
  FunnelSimple,
  MagnifyingGlass,
  WarningCircle,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

/**
 * Fotos de obra — inicio, con dos vistas (como Remato):
 *   Galería → todas las fotos, de la más reciente a la más antigua, con filtros
 *   Obras   → lista de obras para entrar a la galería de cada una
 * Más la captura libre (se elige la obra al terminar) y el estado de la cola.
 * Sirve en /campo/fotos y /admin/fotos.
 */

export function baseFotos(pathname: string) {
  return pathname.startsWith('/admin') ? '/admin/fotos' : '/campo/fotos';
}

export function haceCuanto(iso: string | null): string {
  if (!iso) return '';
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 30) return `hace ${dias} días`;
  return new Date(iso).toLocaleDateString('es-CR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const prefijo = (codigo: string) => codigo.split('-')[0] ?? codigo;

export function FotosPantalla() {
  const abrirCamara = useCamaraFotos();
  const cola = useColaFotos();
  const base = baseFotos(useLocation().pathname);
  const [params, setParams] = useSearchParams();
  const vista = params.get('vista') === 'obras' ? 'obras' : 'galeria';

  function cambiarVista(v: 'galeria' | 'obras') {
    const p = new URLSearchParams(params);
    if (v === 'galeria') p.delete('vista');
    else p.set('vista', v);
    setParams(p, { replace: true });
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fotos de obra</h1>
          <p className="text-sm text-muted-foreground">
            Registro fotográfico del proceso constructivo.
          </p>
        </div>
        <Button size="lg" onClick={() => abrirCamara()}>
          <Camera className="h-5 w-5" weight="bold" aria-hidden="true" />
          Tomar fotos
        </Button>
      </div>

      {(cola.pendientes > 0 || cola.subiendo) && (
        <Card className="flex items-center gap-3 p-3">
          {cola.conError > 0 ? (
            <WarningCircle
              className="h-5 w-5 shrink-0 text-red-600"
              weight="fill"
              aria-hidden="true"
            />
          ) : cola.subiendo ? (
            <CircleNotch
              className="h-5 w-5 shrink-0 animate-spin text-blue-600"
              weight="bold"
              aria-hidden="true"
            />
          ) : (
            <CloudArrowUp
              className="h-5 w-5 shrink-0 text-amber-600"
              weight="bold"
              aria-hidden="true"
            />
          )}
          <p className="flex-1 text-sm">
            <strong>{cola.pendientes}</strong> foto{cola.pendientes === 1 ? '' : 's'} por subir
            {cola.subiendo ? ' · subiendo…' : navigator.onLine ? '' : ' · sin conexión'}
            {cola.conError > 0 && (
              <span className="text-red-700"> · {cola.conError} con error</span>
            )}
          </p>
          {cola.conError > 0 && (
            <Button size="sm" variant="outline" onClick={() => void reintentarErrores()}>
              Reintentar
            </Button>
          )}
        </Card>
      )}

      {/* Pestañas */}
      <div className="grid grid-cols-2 border-b border-border">
        {(['galeria', 'obras'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => cambiarVista(v)}
            className={cn(
              '-mb-px border-b-2 py-2 text-sm font-semibold',
              vista === v
                ? 'border-adelante-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {v === 'galeria' ? 'Galería' : 'Obras'}
          </button>
        ))}
      </div>

      {vista === 'galeria' ? <VistaGaleria base={base} /> : <VistaObras base={base} />}
    </div>
  );
}

// ============================================================================
// Galería general con filtros
// ============================================================================

function VistaGaleria({ base }: { base: string }) {
  const obrasQ = useObrasFotos('');
  const autoresQ = useAutoresFotos();
  const [verFiltros, setVerFiltros] = useState(false);
  const [proyecto, setProyecto] = useState<string | null>(null);
  const [obra, setObra] = useState('');
  const [autor, setAutor] = useState<number | null>(null);
  const [origen, setOrigen] = useState<'' | 'suelta' | 'ficha'>('');
  const [tipo, setTipo] = useState<TipoCentroCosto | ''>('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  // Solo obras con fotos (y sus proyectos) para los filtros.
  const conFotos = useMemo(() => (obrasQ.data ?? []).filter((o) => o.n_fotos > 0), [obrasQ.data]);
  const proyectos = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of conFotos) m.set(prefijo(o.codigo), (m.get(prefijo(o.codigo)) ?? 0) + o.n_fotos);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [conFotos]);
  const obrasFiltro = conFotos
    .filter((o) => !proyecto || prefijo(o.codigo) === proyecto)
    .sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }));

  const filtro: FiltroGaleria = {
    obra: obra || null,
    proyecto: obra ? null : proyecto,
    autor,
    origen: origen || null,
    tipo: tipo || null,
    desde: desde || null,
    hasta: hasta || null,
  };
  const activos = [obra || proyecto, tipo, autor, origen, desde, hasta].filter(Boolean).length;

  function limpiar() {
    setProyecto(null);
    setObra('');
    setAutor(null);
    setOrigen('');
    setTipo('');
    setDesde('');
    setHasta('');
  }

  const SELECT = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm';

  return (
    <div className="space-y-3">
      {/* Proyectos (chips) */}
      {proyectos.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {[['', 0] as [string, number], ...proyectos].map(([p, n]) => {
            const activo = (p || null) === proyecto && !obra;
            return (
              <button
                key={p || 'todos'}
                type="button"
                onClick={() => {
                  setProyecto(p || null);
                  setObra('');
                }}
                className={cn(
                  'shrink-0 rounded-full border px-3 py-1 text-xs font-semibold',
                  activo
                    ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
                    : 'border-border bg-white text-foreground hover:bg-zinc-50',
                )}
              >
                {p || 'Todos'}
                {p && <span className="ml-1 font-normal opacity-70">{n}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setVerFiltros((v) => !v)}>
          <FunnelSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
          Filtros{activos > 0 ? ` (${activos})` : ''}
        </Button>
        {activos > 0 && (
          <Button variant="ghost" size="sm" onClick={limpiar}>
            Limpiar
          </Button>
        )}
      </div>

      {verFiltros && (
        <Card className="grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="space-y-1 text-xs font-medium">
            Obra
            <select className={SELECT} value={obra} onChange={(e) => setObra(e.target.value)}>
              <option value="">{proyecto ? `Todas las de ${proyecto}` : 'Todas las obras'}</option>
              {obrasFiltro.map((o) => (
                <option key={o.codigo} value={o.codigo}>
                  {o.codigo} · {o.nombre ?? ''} ({o.n_fotos})
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs font-medium">
            Tipo de obra
            <select
              className={SELECT}
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoCentroCosto | '')}
            >
              <option value="">Todos</option>
              {Object.entries(TIPOS_CC).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs font-medium">
            Autor
            <select
              className={SELECT}
              value={autor ?? ''}
              onChange={(e) => setAutor(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Todos</option>
              {(autoresQ.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nombre} ({a.n_fotos})
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs font-medium">
            Origen
            <select
              className={SELECT}
              value={origen}
              onChange={(e) => setOrigen(e.target.value as '' | 'suelta' | 'ficha')}
            >
              <option value="">Todas</option>
              <option value="suelta">Fotos de obra</option>
              <option value="ficha">De fichas de control</option>
            </select>
          </label>
          <div className="space-y-1 text-xs font-medium">
            <label htmlFor="fotos-desde">Desde</label>
            <Input
              id="fotos-desde"
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="space-y-1 text-xs font-medium">
            <label htmlFor="fotos-hasta">Hasta</label>
            <Input
              id="fotos-hasta"
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
        </Card>
      )}

      <GaleriaFotos filtro={filtro} base={base} mostrarObra hayFiltros={activos > 0} />
    </div>
  );
}

// ============================================================================
// Lista de obras
// ============================================================================

function VistaObras({ base }: { base: string }) {
  // La búsqueda vive en la URL (?q=) para que siga ahí al volver de una obra.
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [texto, setTexto] = useState(q);
  const obrasQ = useObrasFotos(q);

  useEffect(() => {
    const t = setTimeout(() => {
      const nuevo = texto.trim();
      if (nuevo === q) return;
      const p = new URLSearchParams(params);
      if (nuevo) p.set('q', nuevo);
      else p.delete('q');
      setParams(p, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [texto, q, params, setParams]);

  const obras = obrasQ.data ?? [];
  const conFotos = obras.filter((o) => o.n_fotos > 0);
  const sinFotos = obras.filter((o) => o.n_fotos === 0);

  return (
    <div className="space-y-4">
      <div className="relative">
        <MagnifyingGlass
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          weight="bold"
          aria-hidden="true"
        />
        <Input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Buscar obra o centro de costo…"
          className="pl-8"
        />
      </div>

      {obrasQ.isLoading && (
        <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" /> Cargando
          obras…
        </p>
      )}
      {obrasQ.error && <p className="text-sm text-destructive">Error: {obrasQ.error.message}</p>}

      {[
        { titulo: 'Con fotos', lista: conFotos },
        { titulo: q ? 'Resultados' : 'Otras obras', lista: sinFotos.slice(0, q ? 100 : 40) },
      ].map(
        ({ titulo, lista }) =>
          lista.length > 0 && (
            <section key={titulo} className="space-y-2">
              <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {titulo}
              </h2>
              <ul className="space-y-1.5">
                {lista.map((o) => (
                  <li key={o.codigo}>
                    <Link
                      to={`${base}/${encodeURIComponent(o.codigo)}`}
                      state={{ volver: `${location.pathname}${location.search}` }}
                      className="flex items-center gap-3 rounded-md border border-border bg-white px-3 py-2.5 hover:bg-zinc-50"
                    >
                      <Buildings
                        className="h-5 w-5 shrink-0 text-muted-foreground"
                        weight="bold"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-mono text-sm font-semibold">{o.codigo}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {o.nombre ?? o.area_costeo ?? ''} · {TIPOS_CC[o.tipo]}
                          {!o.habilitada && (
                            <span className="ml-1 text-red-700">
                              · {o.motivo === 'entregada' ? 'entregada' : 'sin fotos nuevas'}
                            </span>
                          )}
                        </span>
                      </span>
                      {o.n_fotos > 0 && (
                        <span className="shrink-0 text-right text-[11px] text-muted-foreground">
                          <span className="block font-semibold text-foreground">
                            {o.n_fotos} fotos
                          </span>
                          {haceCuanto(o.ultima_foto)}
                        </span>
                      )}
                      <CaretRight
                        className="h-4 w-4 shrink-0 text-muted-foreground"
                        weight="bold"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ),
      )}
    </div>
  );
}
